import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { addDays, getDay, startOfToday } from 'date-fns';

import { codeNameToDropdownList, stringToDropdownList } from '@ecp/common/src/utils/good/goodUtil';
import moTheme from '@ecp/common/src/style/theme/mo';
import { FlexBoxColumn, LabelFlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import RadioGroup from '@ecp/common/src/components/radio/mo/RadioGroup';
import { Radio } from '@ecp/common/src/components/radio/mo/Radio';
import Dropdown from '@ecp/common/src/components/dropdown/mo/Dropdown';
import Calendar from '@ecp/common/src/components/calendar/mo/Calendar';
import { dayOfWeekCodeToCalendarCode } from '@ecp/common/src/const/good';
//import theme from '@ecp/common/src/style/theme/default';
import { radioClassNames } from '@ecp/common/src/components/unstyled/input/RadioUnstyled';
import { overrideProperties } from '@ecp/common/src/style/utils';
import { sizePropTypes } from '@ecp/common/src/style/propTypes';

const GoodOptionRegularDelivery = ({
  regularDelivery = {},
  selectedRegularDelivery,
  displayEndDate,
  handleChangeSelectedRegularDelivery,
}) => {
  const { periodList = [], totalList = [], timeList = [], dayOfWeekList = [] } = regularDelivery;
  const { period, total, time, startDateTime } = selectedRegularDelivery;

  const minDeliveryStartDate = addDays(startOfToday(), 3);

  const filterDate = useCallback(
    (date) => {
      const dayOfWeek = dayOfWeekList.map(({ code }) => dayOfWeekCodeToCalendarCode[code]);
      return dayOfWeek.includes(getDay(date));
    },
    [dayOfWeekList]
  );

  const totalListItems = useMemo(() => codeNameToDropdownList(totalList), [totalList]);

  const timeListItems = useMemo(() => stringToDropdownList(timeList), [timeList]);

  return (
    <FlexBoxColumn align-items={'flex-start'} width={'100%'}>
      {periodList.length > 0 && (
        <>
          <Title>배송주기</Title>
          <Spacing top={10} />
          <RadioGroup width={'100%'} flex-wrap={'wrap'} value={period.code}>
            {periodList.map((period) => (
              <Radio
                hideControl
                key={period.code}
                value={period.code}
                onChange={() => handleChangeSelectedRegularDelivery({ period })}
              >
                <RoundRadioButton width={'52px'} htmlFor={period.code}>{period.name}</RoundRadioButton>
              </Radio>
            ))}
          </RadioGroup>
          <Spacing top={20} />
        </>
      )}
      {totalList.length > 0 && (
        <>
          <Title>배송횟수</Title>
          <Spacing top={10} />
          <Dropdown
            items={totalListItems}
            onChange={(total) => handleChangeSelectedRegularDelivery({ total })}
            value={total}
            placeholder={'배송횟수를 선택하세요'}
          />
          <Spacing top={20} />
        </>
      )}
      {timeList.length > 0 && (
        <>
          <Title>배송시간</Title>
          <Spacing top={10} />
          <Dropdown
            items={timeListItems}
            onChange={(time) => handleChangeSelectedRegularDelivery({ time })}
            value={time}
            placeholder={'배송시간을 선택하세요'}
          />
          <Spacing top={20} />
        </>
      )}
      <Title>배송시작일</Title>
      <Spacing top={14} />
      <Calendar
        minDate={minDeliveryStartDate}
        maxDate={new Date(displayEndDate)}
        filterDate={filterDate}
        value={startDateTime}
        placeholderText={'배송시작일을 선택하세요'}
        onChange={(startDateTime) => handleChangeSelectedRegularDelivery({ startDateTime })}
      />
    </FlexBoxColumn>
  );
};

const Title = styled(TextBox)`
  font-weight: ${moTheme.font.weight.bold};
`;

const RoundRadioButton = styled(LabelFlexBoxColumn)`
  background: ${(props) => props.theme.color.background.white};
  cursor: pointer;

  border: 1px solid ${(props) => props.theme.color.line.basic};
  border-radius: 15px;
  margin-right: 6px;
  margin-bottom: 6px;

  &.${radioClassNames.disabled} {
    cursor: default;
  }

  ${overrideProperties(sizePropTypes, {
    width: '58px',
    height: '30px',
  })};

  &.${radioClassNames.checked} {
    color: ${(props) => props.theme.color.text.white};
    background: ${(props) => props.theme.color.third};
    border: 1px solid ${(props) => props.theme.color.third};
    font-weight: ${moTheme.font.weight.medium};
  }
`;

export default GoodOptionRegularDelivery;

GoodOptionRegularDelivery.propTypes = {
  regularDelivery: PropTypes.shape({
    periodList: PropTypes.array,
    totalList: PropTypes.array,
    timeList: PropTypes.array,
    dayOfWeekList: PropTypes.array,
  }),
  selectedRegularDelivery: PropTypes.object,
  displayEndDate: PropTypes.string,
  handleChangeSelectedRegularDelivery: PropTypes.func,
};
